/**
 * AdminMusicalInstruments - Admin dashboard for Musical Instruments category
 * Manages subcategories, providers, listings, bookings with country-specific controls
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { 
  Music, Guitar, Piano, Drum, Mic2, Radio, BookOpen, Wrench,
  Users, ShoppingBag, Calendar, TrendingUp, Settings, 
  Check, X, Clock, MapPin, ArrowLeft, RefreshCw, Loader2,
  BarChart3, Eye, CheckCircle, AlertCircle, Shield, Building
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Subcategory icons mapping
const SUBCATEGORY_ICONS = {
  'guitars-basses': Guitar,
  'keyboards-pianos': Piano,
  'drums-percussion': Drum,
  'string-instruments': Music,
  'wind-instruments': Mic2,
  'traditional-instruments': Drum,
  'dj-studio-equipment': Radio,
  'amps-effects': Radio,
  'accessories-parts': Wrench,
  'sheet-music-books': BookOpen,
  'music-services': Users,
};

// Stat Card Component
function StatCard({ title, value, icon: Icon, color, trend }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {trend && (
              <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? '+' : ''}{trend}% this week
              </p>
            )}
          </div>
          <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Provider Row Component
function ProviderRow({ provider, onApprove, onReject }) {
  const typeColors = {
    music_teacher: 'bg-indigo-100 text-indigo-700',
    repair_technician: 'bg-orange-100 text-orange-700',
    piano_tuner: 'bg-blue-100 text-blue-700',
    rental_shop: 'bg-green-100 text-green-700',
    moving_service: 'bg-purple-100 text-purple-700',
  };
  
  return (
    <div className="flex items-center justify-between p-4 bg-white border rounded-lg mb-2">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <Users className="w-5 h-5 text-gray-500" />
        </div>
        <div>
          <h4 className="font-medium">{provider.display_name}</h4>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={typeColors[provider.provider_type] || 'bg-gray-100'}>
              {provider.provider_type?.replace('_', ' ')}
            </Badge>
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {provider.city}
            </span>
            <Badge variant={provider.country === 'UK' ? 'default' : 'secondary'}>
              {provider.country}
            </Badge>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Badge variant={provider.verification_level > 0 ? 'default' : 'secondary'}>
          Level {provider.verification_level || 0}
        </Badge>
        {provider.status === 'pending' ? (
          <>
            <Button size="sm" variant="outline" className="text-red-600" onClick={() => onReject(provider.provider_id)}>
              <X className="w-4 h-4" />
            </Button>
            <Button size="sm" className="bg-green-600" onClick={() => onApprove(provider.provider_id)}>
              <Check className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <Badge className={provider.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}>
            {provider.status}
          </Badge>
        )}
      </div>
    </div>
  );
}

// Listing Row Component
function ListingRow({ listing, onModerate }) {
  const statusColors = {
    PUBLISHED: 'bg-green-100 text-green-700',
    PENDING: 'bg-amber-100 text-amber-700',
    REJECTED: 'bg-red-100 text-red-700',
  };
  
  return (
    <div className="flex items-center justify-between p-4 bg-white border rounded-lg mb-2">
      <div className="flex items-center gap-4">
        <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
          {listing.media_urls?.[0] ? (
            <img src={listing.media_urls[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <Music className="w-6 h-6 text-gray-400" />
          )}
        </div>
        <div>
          <h4 className="font-medium line-clamp-1">{listing.title}</h4>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <span>{listing.subcategory?.replace('-', ' ')}</span>
            <span>•</span>
            <span>{listing.city}</span>
            <span>•</span>
            <span className="font-medium">{listing.currency === 'LKR' ? 'Rs.' : '£'}{listing.price?.toLocaleString()}</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {listing.moderation?.high_value && (
          <Badge className="bg-amber-500">High Value</Badge>
        )}
        <Badge className={statusColors[listing.moderation?.status] || 'bg-gray-100'}>
          {listing.moderation?.status || 'PENDING'}
        </Badge>
        <Select onValueChange={(value) => onModerate(listing.listing_id, value)}>
          <SelectTrigger className="w-28 h-8">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PUBLISHED">Approve</SelectItem>
            <SelectItem value="REJECTED">Reject</SelectItem>
            <SelectItem value="FLAGGED">Flag</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// Subcategory Config Row
function SubcategoryConfigRow({ category, config, country, onToggle }) {
  const Icon = SUBCATEGORY_ICONS[category] || Music;
  const isEnabled = country === 'UK' ? config.uk_enabled : config.sl_enabled;
  
  return (
    <div className="flex items-center justify-between p-4 bg-white border rounded-lg mb-2">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h4 className="font-medium flex items-center gap-2">
            {config.name}
            {config.is_service_hub && (
              <Badge variant="outline" className="text-xs">Service Hub</Badge>
            )}
          </h4>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <span>{config.listing_types?.length || 0} listing types</span>
            <span>•</span>
            <span>{config.services?.length || 0} services</span>
            {config.sl_featured && country === 'LK' && (
              <Badge className="bg-amber-500 text-xs">Featured</Badge>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch 
            checked={isEnabled}
            onCheckedChange={(checked) => onToggle(category, checked)}
          />
          <Label className="text-sm">{isEnabled ? 'Enabled' : 'Disabled'}</Label>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function AdminMusicalInstruments() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedCountry, setSelectedCountry] = useState('UK');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/music/admin/stats?country=${selectedCountry}`);
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  // Fetch providers
  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/music/providers?country=${selectedCountry}&limit=50`);
      const data = await response.json();
      if (data.success) {
        setProviders(data.providers);
      }
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    }
  };
  
  // Fetch listings
  const fetchListings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/music/listings?country=${selectedCountry}&limit=50`);
      const data = await response.json();
      if (data.success) {
        setListings(data.listings);
      }
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    }
  };
  
  // Fetch category settings
  const fetchCategorySettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/music/admin/category-settings`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch category settings:', error);
    }
  };
  
  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchProviders(),
        fetchListings(),
        fetchCategorySettings(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [selectedCountry]);
  
  // Handle provider approval/rejection
  const handleProviderAction = async (providerId, action) => {
    try {
      // This would call an API endpoint
      toast.success(`Provider ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchProviders();
    } catch (error) {
      toast.error('Action failed');
    }
  };
  
  // Handle listing moderation
  const handleListingModerate = async (listingId, status) => {
    try {
      // This would call an API endpoint
      toast.success(`Listing ${status.toLowerCase()}`);
      fetchListings();
    } catch (error) {
      toast.error('Moderation failed');
    }
  };
  
  // Handle subcategory toggle
  const handleSubcategoryToggle = async (category, enabled) => {
    try {
      toast.success(`${categories[category]?.name} ${enabled ? 'enabled' : 'disabled'} for ${selectedCountry}`);
      // This would call an API endpoint
    } catch (error) {
      toast.error('Toggle failed');
    }
  };
  
  // Reseed data
  const handleReseed = async () => {
    try {
      const response = await fetch(`${API_URL}/api/music/seed`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(`Seeded ${data.providers_count} providers and ${data.listings_count} listings`);
        // Refresh data
        fetchStats();
        fetchProviders();
        fetchListings();
      }
    } catch (error) {
      toast.error('Seed failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10"
                onClick={() => navigate('/admin/dashboard')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Music className="w-7 h-7" />
                  Musical Instruments Admin
                </h1>
                <p className="text-indigo-200">Manage categories, providers, and listings</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Country Selector */}
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white" data-testid="country-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">
                    <span className="flex items-center gap-2">🇬🇧 United Kingdom</span>
                  </SelectItem>
                  <SelectItem value="LK">
                    <span className="flex items-center gap-2">🇱🇰 Sri Lanka</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={handleReseed}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reseed Data
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard 
                title="Providers" 
                value={stats?.providers || 0} 
                icon={Users} 
                color="bg-indigo-500"
              />
              <StatCard 
                title="Active Listings" 
                value={stats?.listings || 0} 
                icon={ShoppingBag} 
                color="bg-purple-500"
              />
              <StatCard 
                title="Total Bookings" 
                value={stats?.bookings || 0} 
                icon={Calendar} 
                color="bg-pink-500"
              />
              <StatCard 
                title="Pending Bookings" 
                value={stats?.pending_bookings || 0} 
                icon={Clock} 
                color="bg-amber-500"
              />
            </div>
            
            {/* Country Stats */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Listings by Country</h3>
                    <p className="text-sm text-gray-500">Distribution across regions</p>
                  </div>
                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-indigo-600">{stats?.by_country?.UK || 0}</p>
                      <p className="text-sm text-gray-500">🇬🇧 UK</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{stats?.by_country?.LK || 0}</p>
                      <p className="text-sm text-gray-500">🇱🇰 Sri Lanka</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white shadow-sm mb-6">
                <TabsTrigger value="overview" className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="categories" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Categories
                </TabsTrigger>
                <TabsTrigger value="providers" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Providers
                </TabsTrigger>
                <TabsTrigger value="listings" className="flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4" />
                  Listings
                </TabsTrigger>
              </TabsList>
              
              {/* Overview Tab */}
              <TabsContent value="overview">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Subcategory Stats */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Listings by Subcategory</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {stats?.by_subcategory && Object.entries(stats.by_subcategory)
                          .filter(([_, count]) => count > 0)
                          .sort((a, b) => b[1] - a[1])
                          .map(([slug, count]) => {
                            const Icon = SUBCATEGORY_ICONS[slug] || Music;
                            return (
                              <div key={slug} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Icon className="w-4 h-4 text-indigo-600" />
                                  <span className="text-sm capitalize">{slug.replace(/-/g, ' ')}</span>
                                </div>
                                <Badge variant="secondary">{count}</Badge>
                              </div>
                            );
                          })}
                        {(!stats?.by_subcategory || Object.values(stats.by_subcategory).every(v => v === 0)) && (
                          <p className="text-sm text-gray-500 text-center py-4">No listings yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Quick Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button 
                        className="w-full justify-start" 
                        variant="outline"
                        onClick={() => setActiveTab('providers')}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Review Pending Providers
                      </Button>
                      <Button 
                        className="w-full justify-start" 
                        variant="outline"
                        onClick={() => setActiveTab('listings')}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Moderate Listings
                      </Button>
                      <Button 
                        className="w-full justify-start" 
                        variant="outline"
                        onClick={() => setActiveTab('categories')}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Configure Categories
                      </Button>
                      <Button 
                        className="w-full justify-start bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => navigate('/music')}
                      >
                        <Music className="w-4 h-4 mr-2" />
                        View Public Page
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              {/* Categories Tab */}
              <TabsContent value="categories">
                <Card>
                  <CardHeader>
                    <CardTitle>Category Configuration</CardTitle>
                    <CardDescription>
                      Enable or disable subcategories for {selectedCountry === 'UK' ? 'United Kingdom' : 'Sri Lanka'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Country-Specific Rules Info */}
                    <Card className="mb-4 bg-blue-50 border-blue-200">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-blue-800 mb-2">Country-Specific Rules ({selectedCountry})</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-blue-600 font-medium">Verification Requirements</p>
                            <ul className="mt-1 text-blue-700 list-disc list-inside">
                              {selectedCountry === 'UK' ? (
                                <>
                                  <li>Business verified for repairs</li>
                                  <li>DBS check for teachers</li>
                                  <li>High deposits required</li>
                                </>
                              ) : (
                                <>
                                  <li>ID verified allowed</li>
                                  <li>Skilled individuals OK</li>
                                  <li>Flexible deposits</li>
                                </>
                              )}
                            </ul>
                          </div>
                          <div>
                            <p className="text-blue-600 font-medium">Highlighted Categories</p>
                            <ul className="mt-1 text-blue-700 list-disc list-inside">
                              {selectedCountry === 'UK' ? (
                                <>
                                  <li>Online lessons popular</li>
                                  <li>Limited trade-ins</li>
                                  <li>Standard rentals</li>
                                </>
                              ) : (
                                <>
                                  <li>Traditional instruments featured</li>
                                  <li>Trade-ins popular</li>
                                  <li>In-person lessons preferred</li>
                                </>
                              )}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {Object.entries(categories).map(([slug, config]) => (
                      <SubcategoryConfigRow 
                        key={slug}
                        category={slug}
                        config={config}
                        country={selectedCountry}
                        onToggle={handleSubcategoryToggle}
                      />
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Providers Tab */}
              <TabsContent value="providers">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Service Providers</CardTitle>
                      <CardDescription>Manage music service providers</CardDescription>
                    </div>
                    <Badge variant="secondary">{providers.length} providers</Badge>
                  </CardHeader>
                  <CardContent>
                    {providers.length > 0 ? (
                      providers.map(provider => (
                        <ProviderRow 
                          key={provider.provider_id}
                          provider={provider}
                          onApprove={(id) => handleProviderAction(id, 'approve')}
                          onReject={(id) => handleProviderAction(id, 'reject')}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No providers found for {selectedCountry}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Listings Tab */}
              <TabsContent value="listings">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Instrument Listings</CardTitle>
                      <CardDescription>Moderate and manage listings</CardDescription>
                    </div>
                    <Badge variant="secondary">{listings.length} listings</Badge>
                  </CardHeader>
                  <CardContent>
                    {listings.length > 0 ? (
                      listings.map(listing => (
                        <ListingRow 
                          key={listing.listing_id}
                          listing={listing}
                          onModerate={handleListingModerate}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No listings found for {selectedCountry}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
